import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";

const powderCoatingBenefits = [
  "Clean appearance", "Durable surface finish", "Consistent coating",
  "Suitable for fabricated metal parts", "Available in project-required colors", "Improves presentation and protection",
];

const epoxyCoatingBenefits = [
  "Protective coating layer", "Enhanced durability", "Suitable for demanding environments",
  "Useful for specific project requirements", "Improves service life of coated components",
];

const coatingApplications = [
  "Fabricated brackets", "Support frames", "Steel covers", "Equipment supports",
  "Panels and plates", "Custom fabricated items", "Industrial accessories", "Project-specific coated materials",
];

export default function Coating() {
  const { t } = useLanguage();

  return (
    <>
      <PageHeader titleKey="coatingTitle" subtitleKey="coatingSubtitle" />

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
              <div className="relative mb-6">
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gold rounded-full" />
                <img src="/images/powder-coating.jpg" alt="Powder coating" className="w-full rounded-lg aspect-[16/10] object-cover ml-[3px]" loading="lazy" />
              </div>
              <p className="label-overline text-gold mb-2">POWDER COATING</p>
              <h2 className="font-display font-bold text-2xl text-primary-deep mb-4">{t("powderCoating")}</h2>
              <p className="text-steel leading-relaxed mb-5">{t("powderCoatingDesc")}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-deep mb-2">{t("benefits")}</p>
              <ul className="space-y-1.5">
                {powderCoatingBenefits.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                    <span className="text-sm text-steel">{b}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.15 }}>
              <div className="relative mb-6">
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-secondary-blue rounded-full" />
                <img src="/images/epoxy-coating.jpg" alt="Epoxy coating" className="w-full rounded-lg aspect-[16/10] object-cover ml-[3px]" loading="lazy" />
              </div>
              <p className="label-overline text-secondary-blue mb-2">EPOXY COATING</p>
              <h2 className="font-display font-bold text-2xl text-primary-deep mb-4">{t("epoxyCoating")}</h2>
              <p className="text-steel leading-relaxed mb-5">{t("epoxyCoatingDesc")}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-deep mb-2">{t("benefits")}</p>
              <ul className="space-y-1.5">
                {epoxyCoatingBenefits.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary-blue shrink-0" />
                    <span className="text-sm text-steel">{b}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <SectionHeading overline="APPLICATIONS" titleKey="coatingApplications" align="center" />
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {coatingApplications.map((app, i) => (
              <motion.div key={app} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5, delay: i * 0.08 }} className="bg-white rounded-lg p-5 text-center border border-muted-border transition-all duration-300 hover:shadow-card-hover">
                <div className="w-10 h-10 rounded-full bg-primary-deep/10 flex items-center justify-center mx-auto mb-3">
                  <span className="w-2.5 h-2.5 rounded-full bg-primary-deep" />
                </div>
                <p className="text-sm font-medium text-graphite">{app}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection titleKey="needCoating" descKey="needCoatingDesc" buttons={[{ labelKey: "sendCoatingInquiry", href: "/contact", type: "white" }]} bgColor="dark" />
    </>
  );
}
